import React, { forwardRef } from 'react';
import { Checkbox, FormControlLabel, SxProps, Typography } from '@mui/material';
import { color } from '@/theme/tokens';
import IconCheckBoxFilled from '@/assets/icons/IconCheckBoxFilled';
import IconCheckBoxOutlineBlank from '@/assets/icons/IconCheckBoxOutlineBlank';
import IconCheckBoxIndeterminate from '@/assets/icons/IconCheckBoxIndeterminate';

type CheckboxProps = {
    // A string gets the standard label5medium wrapping; a node renders as-is
    // (e.g. a title + description stack that stays centered against the control).
    label: React.ReactNode;
    checked: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    labelFontSize?: string;
    labelColor?: string;
    checkboxColor?: string;
    sx?: SxProps;
    disabled?: boolean;
    indeterminate?: boolean;
    ringInset?: string;
    ringRadius?: string;
};

const CustomCheckbox = forwardRef<HTMLInputElement, CheckboxProps>(
    (
        {
            label,
            checked,
            onChange,
            sx,
            disabled = false,
            indeterminate = false,
            checkboxColor = color.gray[80],
            ringInset = '8px',
            ringRadius = '4px',
        },
        ref
    ) => {
        return (
            <FormControlLabel
                control={
                    <Checkbox
                        indeterminate={indeterminate}
                        checked={checked}
                        onChange={onChange}
                        disabled={disabled}
                        disableRipple
                        checkedIcon={<IconCheckBoxFilled color={checkboxColor} width={17} height={17} />}
                        icon={<IconCheckBoxOutlineBlank color={checkboxColor} width={17} height={17} />}
                        indeterminateIcon={<IconCheckBoxIndeterminate color={checkboxColor} width={17} height={17} />}
                        sx={{
                            width: '46px',
                            height: '46px',
                            // A long node label must wrap instead of squeezing the control,
                            // or the checkbox column drifts from row to row.
                            flexShrink: 0,
                            padding: '8px',
                            color: checkboxColor,
                            '&.Mui-checked': {
                                color: checkboxColor,
                            },
                            '&.MuiCheckbox-indeterminate': {
                                color: checkboxColor,
                            },
                            '&:hover': {
                                backgroundColor: color.black[5],
                            },
                            '&:active': {
                                backgroundColor: color.black[10],
                            },
                            '&.Mui-focusVisible::after': {
                                content: '""',
                                position: 'absolute',
                                inset: ringInset,
                                border: `2px solid ${color.gray[80]}`,
                                borderRadius: ringRadius,
                                pointerEvents: 'none',
                            },
                            ...sx,
                        }}
                    />
                }
                label={
                    typeof label === 'string' ? (
                        <Typography variant="label5medium" color={color.gray[80]} sx={{ opacity: disabled ? 0.4 : 1 }}>
                            {label}
                        </Typography>
                    ) : (
                        label
                    )
                }
                ref={ref}
                sx={{
                    '& .MuiCheckbox-root': {
                        opacity: disabled ? 0.4 : 1,
                    },
                }}
            />
        );
    }
);

CustomCheckbox.displayName = 'CustomCheckbox';

export default CustomCheckbox;
